/* eslint-disable quote-props */

import request, { type SuperAgentRequest } from 'superagent';
import { getStoredAccessToken } from 'pages/Auth/utils';

// services/base/index.js
// BaseService communicates with the API.
// This version is a revision based on Mauricio Sanchez <mausadd@outlook.com>
// implementation and later adapted in this codebase by clenondavis <dev@carloslenon.com>.
type RequestHeaders = Record<string, string>;

// Compose request headers, including authorization when applicable.
const buildHeaders = (defaultHeaders: RequestHeaders, userHeaders?: RequestHeaders): RequestHeaders => ({
  ...defaultHeaders,
  ...userHeaders,
});

const getAccessToken = (): string | undefined => getStoredAccessToken() ?? undefined;

const withBearerAuth = <TResponse>(requestBuilder: SuperAgentRequest<TResponse>) => {
  const accessToken = getAccessToken();

  return accessToken ? requestBuilder.auth(accessToken, { type: 'bearer' }) : requestBuilder;
};

class BaseService {
  protected defaultHeaders: RequestHeaders = { 'Content-Type': 'application/json' };

  // Perform a GET request to the API.
  async get<T>(url: string, headers?: RequestHeaders): Promise<T> {
    try {
      const response = await withBearerAuth(request.get(url).set(buildHeaders(this.defaultHeaders, headers)));
      return response.body as T;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a file download request to the API.
  async getFile<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: RequestHeaders
  ): Promise<TResponse> {
    try {
      const response = await withBearerAuth(request.post(url).responseType('blob').set(buildHeaders(this.defaultHeaders, headers))).send(data);
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a PUT request to the API.
  async put<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: RequestHeaders
  ): Promise<TResponse> {
    try {
      const response = await withBearerAuth(request.put(url).set(buildHeaders(this.defaultHeaders, headers))).send(data);
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a POST request to the API.
  async post<TResponse = unknown, TPayload = unknown>(
    url: string,
    data?: TPayload,
    headers?: RequestHeaders
  ): Promise<TResponse> {
    try {
      const response = await withBearerAuth(request.post(url).set(buildHeaders(this.defaultHeaders, headers))).send(data);
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a DELETE request to the API.
  async delete<TResponse = unknown>(url: string, headers?: RequestHeaders): Promise<TResponse> {
    try {
      const response = await withBearerAuth(request.del(url).set(buildHeaders(this.defaultHeaders, headers)));
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a POST request without authorization headers.
  async postNoAuth<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: RequestHeaders
  ): Promise<TResponse> {
    try {
      const response = await request.post(url).set(buildHeaders(this.defaultHeaders, headers)).send(data);
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a GET request without authorization headers.
  async getNoAuth<TResponse = unknown>(url: string, _query?: unknown, headers?: RequestHeaders): Promise<TResponse> {
    try {
      const response = await request.get(url).set(buildHeaders(this.defaultHeaders, headers));
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Perform a PATCH request to the API.
  async patch<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: RequestHeaders
  ): Promise<TResponse> {
    try {
      const response = await withBearerAuth(request.patch(url).set(buildHeaders(this.defaultHeaders, headers))).send(data);
      return response.body as TResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

export default BaseService;
